/*
 * @Author: AI Assistant
 * @Date: 2026-01-15
 * @Description: PWA 添加到桌面提示卡片的全局状态管理
 * @FilePath: /weryai-web/composables/usePwaPrompt.ts
 * 
 * 自动添加到桌面提示和任务上报均已下线；保留用户主动操作时的安装引导。
 */

import { analyticsEvent } from '~/utils/log-event-func'

// 全局状态：是否显示 PWA 提示卡片
const pwaPromptVisible = ref(false)

// 全局状态：保存 beforeinstallprompt 事件（用于 Android 一键安装）
let deferredPrompt: any = null

// 全局状态：引导弹窗是否显示
const pwaGuideModalVisible = ref(false)

export const usePwaPrompt = () => {
    /**
     * 检测是否已从主屏幕打开（PWA 模式）
     */
    const isRunningAsPwa = (): boolean => {
        if (!import.meta.client) return false
        
        // 方法1: 检测 display-mode: standalone
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        
        // 方法2: iOS Safari 特有属性
        const isIosStandalone = (window.navigator as any).standalone === true
        
        return isStandalone || isIosStandalone
    }
    
    /**
     * 检测是否已添加到主屏幕
     * 通过本地存储标记判断
     */
    const isAddedToDesktop = (): boolean => {
        if (!import.meta.client) return false
        return localStorage.getItem('pwa_added_to_desktop') === 'true'
    }
    
    /**
     * 标记已添加到主屏幕
     */
    const markAsAddedToDesktop = () => {
        if (!import.meta.client) return
        localStorage.setItem('pwa_added_to_desktop', 'true')
    }
    
    /**
     * 检测是否为移动设备
     */
    const isMobile = (): boolean => {
        if (!import.meta.client) return false
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }
    
    /**
     * 检测是否为 Android 设备
     */
    const isAndroid = (): boolean => {
        if (!import.meta.client) return false
        return /Android/i.test(navigator.userAgent)
    }
    
    /**
     * 初始化 beforeinstallprompt 事件监听
     * 用于 Android 设备的一键安装功能
     * 应在 app 入口处调用一次
     */
    const initBeforeInstallPrompt = () => {
        if (!import.meta.client) return
        
        window.addEventListener('beforeinstallprompt', (e: Event) => {
            // 阻止浏览器默认的安装提示
            e.preventDefault()
            // 保存事件供后续使用
            deferredPrompt = e
            console.log('✅ beforeinstallprompt 事件已捕获')
        })
        
        // 监听安装完成事件
        window.addEventListener('appinstalled', () => {
            console.log('✅ PWA 已安装到设备')
            deferredPrompt = null
            markAsAddedToDesktop()
        })
    }
    
    /**
     * 检测是否可以触发原生安装提示
     * 只有 Android Chrome 等支持 beforeinstallprompt 的浏览器才返回 true
     */
    const canPromptInstall = (): boolean => {
        return deferredPrompt !== null
    }
    
    /**
     * 触发原生安装提示（Android）
     * @returns Promise<boolean> 用户是否同意安装
     */
    const promptInstall = async (): Promise<boolean> => {
        if (!deferredPrompt) {
            console.log('❌ 无法触发安装提示：deferredPrompt 为空')
            return false
        }
        
        try {
            // 触发原生安装弹窗
            deferredPrompt.prompt()
            
            // 等待用户选择
            const { outcome } = await deferredPrompt.userChoice
            console.log(`用户选择: ${outcome}`)
            
            // 清空保存的事件（只能使用一次）
            deferredPrompt = null
            
            if (outcome === 'accepted') {
                console.log('✅ 用户同意安装 PWA')
                markAsAddedToDesktop()
                return true
            } else {
                console.log('❌ 用户拒绝安装 PWA')
                return false
            }
        } catch (error) {
            console.error('❌ 触发安装提示失败:', error)
            return false
        }
    }
    
    /**
     * 关闭 PWA 提示卡片
     */
    const closePwaPrompt = () => {
        pwaPromptVisible.value = false
    }
    
    /**
     * 打开 PWA 引导弹窗
     */
    const openPwaGuideModal = () => {
        pwaPromptVisible.value = false  // 先关闭提示卡片
        pwaGuideModalVisible.value = true
        // 埋点：看到添加到桌面引导弹窗
        analyticsEvent('view_add_home_guide')
    }
    
    /**
     * 关闭 PWA 引导弹窗
     */
    const closePwaGuideModal = () => {
        pwaGuideModalVisible.value = false
    }
    
    return {
        // 状态
        pwaPromptVisible,
        pwaGuideModalVisible,
        
        // 检测方法
        isMobile,
        isAndroid,
        isRunningAsPwa,
        isAddedToDesktop,
        canPromptInstall,
        
        // 操作方法
        closePwaPrompt,
        openPwaGuideModal,
        closePwaGuideModal,
        markAsAddedToDesktop,
        initBeforeInstallPrompt,
        promptInstall,
    }
}
